import React, { useEffect, useState } from 'react';
import { Loader2, Save, Target } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';

/**
 * AdminStrategicGoals — editor for `settings.strategic_goals.pillars`.
 *
 * Shape stored in Mongo:
 *   { pillars: [
 *       { title_ar, title_en, items: [ {ar, en}, {ar, en}, {ar, en} ] },
 *       ...
 *   ] }
 *
 * Leaving any bilingual field blank keeps the built-in default from the
 * public component (StrategicGoals.jsx) — the site never breaks.
 */

const PILLARS_META = [
  {
    label_ar: 'الركيزة الأولى — الريادة والامتثال البيئي',
    label_en: 'Pillar 1 — Environmental Leadership and Compliance',
  },
  {
    label_ar: 'الركيزة الثانية — الابتكار والتميز العلمي',
    label_en: 'Pillar 2 — Innovation and Scientific Excellence',
  },
  {
    label_ar: 'الركيزة الثالثة — الأثر الوطني والمسؤولية المجتمعية',
    label_en: 'Pillar 3 — National Impact and Social Responsibility',
  },
  {
    label_ar: 'الركيزة الرابعة — السلامة والاستدامة التشغيلية',
    label_en: 'Pillar 4 — Operational Safety and Sustainability',
  },
];
const ITEMS_PER_PILLAR = 3;

const inputCls =
  'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

const Field = ({ label, dir, children }) => (
  <div>
    <label
      dir={dir}
      className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
    >
      {label}
    </label>
    {children}
  </div>
);

const emptyPillar = () => ({
  title_ar: '',
  title_en: '',
  items: Array.from({ length: ITEMS_PER_PILLAR }, () => ({ ar: '', en: '' })),
});

const normalize = (pillars) => {
  const arr = Array.isArray(pillars) ? pillars.slice(0, PILLARS_META.length) : [];
  return PILLARS_META.map((_, i) => {
    const src = arr[i] || {};
    const items = Array.isArray(src.items) ? src.items.slice(0, ITEMS_PER_PILLAR) : [];
    return {
      title_ar: src.title_ar || '',
      title_en: src.title_en || '',
      items: Array.from({ length: ITEMS_PER_PILLAR }, (_, j) => ({
        ar: items[j]?.ar || '',
        en: items[j]?.en || '',
      })),
    };
  });
};

const AdminStrategicGoals = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [pillars, setPillars] = useState(() => normalize([]));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.get('/settings/strategic_goals');
        if (!alive) return;
        setPillars(normalize(data?.value?.pillars));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const patchPillar = (idx, patch) => {
    setPillars((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const patchItem = (pIdx, iIdx, patch) => {
    setPillars((prev) => prev.map((p, i) => {
      if (i !== pIdx) return p;
      return { ...p, items: p.items.map((it, j) => (j === iIdx ? { ...it, ...patch } : it)) };
    }));
  };

  const onSave = async () => {
    setSaving(true); setError(''); setMsg('');
    try {
      await adminApi.put('/settings/strategic_goals', { value: { pillars } });
      setMsg(isAr ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-brand-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-strategic-goals-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className={isAr ? 'text-right' : 'text-left'}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {isAr ? 'الأهداف الاستراتيجية' : 'Strategic Goals'}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {isAr
            ? 'حرّر ركيزتَي الأهداف الاستراتيجية وعناصر كل ركيزة. اترك أي حقل فارغاً لاستخدام النص الافتراضي.'
            : 'Edit the two Strategic Goals pillars and their bullet items. Leave any field blank to use the built-in default.'}
        </p>
      </div>

      {error && (
        <div className="px-4 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}
      {msg && (
        <div className="px-4 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-sm">
          {msg}
        </div>
      )}

      {pillars.map((pillar, pIdx) => {
        const meta = PILLARS_META[pIdx];
        return (
          <section
            key={pIdx}
            data-testid={`strategic-pillar-editor-${pIdx}`}
            className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4"
          >
            <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
              <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue/15 to-brand-green/15 border border-brand-blue/25 flex items-center justify-center">
                <Target className="w-5 h-5 text-brand-blue-700 dark:text-brand-blue-300" />
              </span>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  {isAr ? meta.label_ar : meta.label_en}
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {ITEMS_PER_PILLAR} {isAr ? 'نقاط' : 'bullet items'}
                </p>
              </div>
            </div>

            {/* Pillar title */}
            <div className={`rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-3 ${isAr ? 'text-right' : 'text-left'}`}>
              <div className={`text-[11px] font-bold text-brand-blue-700 dark:text-brand-blue-300 mb-2 ${isAr ? 'text-right' : ''}`}>
                {isAr ? 'عنوان الركيزة' : 'Pillar title'}
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                <Field label={isAr ? 'عربي' : 'Arabic'} dir="rtl">
                  <input
                    dir="rtl"
                    className={inputCls}
                    value={pillar.title_ar}
                    onChange={(e) => patchPillar(pIdx, { title_ar: e.target.value })}
                    data-testid={`pillar-${pIdx}-title-ar`}
                  />
                </Field>
                <Field label={isAr ? 'إنجليزي' : 'English'} dir="ltr">
                  <input
                    dir="ltr"
                    className={inputCls}
                    value={pillar.title_en}
                    onChange={(e) => patchPillar(pIdx, { title_en: e.target.value })}
                    data-testid={`pillar-${pIdx}-title-en`}
                  />
                </Field>
              </div>
            </div>

            {/* Bullet items */}
            {pillar.items.map((item, iIdx) => (
              <div
                key={iIdx}
                className={`rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-3 ${isAr ? 'text-right' : 'text-left'}`}
              >
                <div className={`text-[11px] font-bold text-brand-blue-700 dark:text-brand-blue-300 mb-2 ${isAr ? 'text-right' : ''}`}>
                  {isAr ? `النقطة ${iIdx + 1}` : `Bullet ${iIdx + 1}`}
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  <Field label={isAr ? 'عربي' : 'Arabic'} dir="rtl">
                    <textarea
                      rows={2}
                      dir="rtl"
                      className={inputCls}
                      value={item.ar}
                      onChange={(e) => patchItem(pIdx, iIdx, { ar: e.target.value })}
                      data-testid={`pillar-${pIdx}-item-${iIdx}-ar`}
                    />
                  </Field>
                  <Field label={isAr ? 'إنجليزي' : 'English'} dir="ltr">
                    <textarea
                      rows={2}
                      dir="ltr"
                      className={inputCls}
                      value={item.en}
                      onChange={(e) => patchItem(pIdx, iIdx, { en: e.target.value })}
                      data-testid={`pillar-${pIdx}-item-${iIdx}-en`}
                    />
                  </Field>
                </div>
              </div>
            ))}
          </section>
        );
      })}

      {/* Save */}
      <div className={`flex ${isAr ? 'justify-start' : 'justify-end'}`}>
        <button
          type="button"
          data-testid="strategic-goals-save"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isAr ? 'حفظ الأهداف الاستراتيجية' : 'Save Strategic Goals'}
        </button>
      </div>
    </div>
  );
};

export default AdminStrategicGoals;
